import {
  engineerStatusFor,
  isExcludedFromActive,
} from "./engineerStatus";

export const RECONCILIATION_ORDER = [
  "unresolved",
  "review",
  "new",
  "suggestion",
  "inactive",
  "exact",
  "alias",
  "rota-only",
];

const VALID_STATUSES = new Set(["active", "left", "admin"]);

export function normalizeReconciliationName(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function clean(value, fallback = "") {
  const text = String(value || "").trim();
  return text || fallback;
}

function aliasList(row) {
  return (row?.aliases || [])
    .map((entry) => (typeof entry === "string" ? entry : entry?.alias))
    .map((alias) => clean(alias))
    .filter(Boolean);
}

function targetStatus(target, statusIndex) {
  const explicit = String(target?.status || "")
    .trim()
    .toLowerCase();
  if (VALID_STATUSES.has(explicit)) return explicit;
  return engineerStatusFor(target?.engineer, statusIndex);
}

function targetIsInactive(target, statusIndex) {
  const status = targetStatus(target, statusIndex);
  return status === "left" || status === "admin";
}

function addCandidate(index, key, target) {
  if (!key) return;
  const list = index[key] || [];
  if (!list.some((entry) => entry.engineer === target.engineer)) {
    list.push(target);
  }
  index[key] = list;
}

function buildTargetIndexes(targets, statusIndex) {
  const canonical = {};
  const aliases = {};
  const validTargets = [];

  (targets || []).forEach((rawTarget) => {
    const engineer = clean(rawTarget?.engineer);
    const key = normalizeReconciliationName(engineer);
    if (!engineer || !key) return;
    const target = {
      ...rawTarget,
      engineer,
      aliases: aliasList(rawTarget),
      status: targetStatus(rawTarget, statusIndex),
      key,
    };
    validTargets.push(target);
    addCandidate(canonical, key, target);
    target.aliases.forEach((alias) => {
      addCandidate(aliases, normalizeReconciliationName(alias), target);
    });
  });

  return { canonical, aliases, validTargets };
}

function buildSuggestionIndex(suggestions) {
  const index = {};
  (suggestions || []).forEach((suggestion) => {
    const key = normalizeReconciliationName(suggestion?.alias);
    if (!key) return;
    if (!index[key]) index[key] = [];
    index[key].push(suggestion);
  });
  return index;
}

function rowAction(classification) {
  if (classification === "suggestion") return "Apply high-confidence alias";
  if (classification === "review") return "Review candidate";
  if (classification === "inactive") return "Excluded by engineer status";
  if (classification === "unresolved") return "Add alias or correct the source";
  if (classification === "new") return "Added to the roster on save";
  return "None";
}

function rowEvidence(classification, target, suggestion, occurrences) {
  if (classification === "exact") {
    return `Exact canonical match; ${occurrences} source occurrence${occurrences === 1 ? "" : "s"}.`;
  }
  if (classification === "alias") {
    return `Saved alias for ${target.engineer}; ${occurrences} source occurrence${occurrences === 1 ? "" : "s"}.`;
  }
  if (classification === "suggestion") {
    return suggestion?.evidence || "One high-confidence candidate.";
  }
  if (classification === "review") {
    return suggestion?.evidence || "More than one possible identity or manual review required.";
  }
  if (classification === "inactive") {
    return `${target?.engineer || "This name"} is marked ${target?.status || "inactive"}.`;
  }
  if (classification === "new") {
    return `No existing engineer is close to this name; treated as a new starter (${occurrences} booking${occurrences === 1 ? "" : "s"}).`;
  }
  if (classification === "rota-only") {
    return "Active rota engineer had no matching source name.";
  }
  return "No canonical engineer or saved alias matched this name.";
}

function makeRow({
  name,
  engineer = "",
  classification,
  occurrences = 0,
  source = "source",
  status = "active",
  reason = "",
  suggestion = null,
  target = null,
}) {
  return {
    id: `${classification}-${normalizeReconciliationName(name) || "unknown"}`,
    name: clean(name, "Unknown"),
    engineer: clean(engineer),
    classification,
    occurrences,
    source,
    status,
    reason: clean(reason),
    evidence: rowEvidence(classification, target, suggestion, occurrences),
    action: rowAction(classification),
  };
}

export function buildReconciliationReport({
  sourceNames = [],
  targets = [],
  rotaTargets = [],
  suggestions = [],
  targetSource = "unavailable",
  statusIndex = null,
  // Repair Centre uploads register unmatched names as new engineers on save.
  // History has nothing to register, so it keeps calling them unresolved.
  classifyNew = false,
} = {}) {
  const { canonical, aliases, validTargets } = buildTargetIndexes(
    targets,
    statusIndex,
  );
  const suggestionIndex = buildSuggestionIndex(suggestions);
  const available = targetSource !== "unavailable" && validTargets.length > 0;
  const sourceGroups = new Map();
  let totalOccurrences = 0;

  (sourceNames || []).forEach((rawName) => {
    const name = clean(rawName);
    const key = normalizeReconciliationName(name);
    if (!key) return;
    totalOccurrences += 1;
    const existing = sourceGroups.get(key);
    if (existing) {
      existing.occurrences += 1;
    } else {
      sourceGroups.set(key, { name, key, occurrences: 1 });
    }
  });

  const rows = [];
  const seenTargetKeys = new Set();

  sourceGroups.forEach((group) => {
    const exactCandidates = canonical[group.key] || [];
    const aliasCandidates = aliases[group.key] || [];
    const suggestionsForName = suggestionIndex[group.key] || [];
    let classification = "unresolved";
    let target = null;
    let suggestion = null;
    let reason = "No identity match";

    if (exactCandidates.length === 1) {
      target = exactCandidates[0];
      classification = targetIsInactive(target, statusIndex)
        ? "inactive"
        : "exact";
      reason = classification === "inactive" ? "Inactive engineer" : "Canonical name";
    } else if (exactCandidates.length > 1) {
      classification = "review";
      reason = "Duplicate canonical target";
      target = exactCandidates[0];
    } else if (aliasCandidates.length === 1) {
      target = aliasCandidates[0];
      classification = targetIsInactive(target, statusIndex)
        ? "inactive"
        : "alias";
      reason = classification === "inactive" ? "Inactive engineer alias" : "Saved alias";
    } else if (aliasCandidates.length > 1) {
      classification = "review";
      reason = "Ambiguous saved alias";
      target = aliasCandidates[0];
    } else if (suggestionsForName.length > 1) {
      classification = "review";
      reason = "Ambiguous suggestion";
      suggestion = suggestionsForName[0];
      target = validTargets.find(
        (entry) => entry.engineer === suggestion.engineer,
      );
    } else if (suggestionsForName.length === 1) {
      suggestion = suggestionsForName[0];
      target = validTargets.find(
        (entry) => entry.engineer === suggestion.engineer,
      );
      classification = suggestion.autoApply ? "suggestion" : "review";
      reason = suggestion.reason || "Suggested identity";
    } else if (isExcludedFromActive(group.name, statusIndex)) {
      classification = "inactive";
      reason = "Inactive engineer status";
    } else if (classifyNew && available) {
      // Nothing matches and nothing is even close: a new starter, not a
      // misspelling. Close-but-unsure names land in "review" above instead,
      // which is what stops a renamed engineer becoming a phantom.
      classification = "new";
      reason = "New engineer";
    }

    if (target) seenTargetKeys.add(target.key);
    rows.push(
      makeRow({
        name: group.name,
        engineer: target?.engineer || suggestion?.engineer || "",
        classification,
        occurrences: group.occurrences,
        status: target?.status || engineerStatusFor(group.name, statusIndex),
        reason,
        suggestion,
        target,
      }),
    );
  });

  const rotaSeen = new Set();
  (rotaTargets || []).forEach((rawTarget) => {
    const engineer = clean(rawTarget?.engineer);
    const key = normalizeReconciliationName(engineer);
    if (!engineer || !key || rotaSeen.has(key)) return;
    rotaSeen.add(key);
    const status = targetStatus(rawTarget, statusIndex);
    if (status !== "active" || seenTargetKeys.has(key)) return;
    rows.push(
      makeRow({
        name: engineer,
        engineer,
        classification: "rota-only",
        source: "rota",
        status,
        reason: "Rota-only engineer",
        target: { ...rawTarget, engineer, status },
      }),
    );
  });

  const order = new Map(RECONCILIATION_ORDER.map((value, index) => [value, index]));
  rows.sort(
    (a, b) =>
      (order.get(a.classification) ?? 99) -
        (order.get(b.classification) ?? 99) ||
      normalizeReconciliationName(a.name).localeCompare(
        normalizeReconciliationName(b.name),
      ),
  );

  const counts = {};
  RECONCILIATION_ORDER.forEach((classification) => {
    counts[classification] = 0;
  });
  rows.forEach((row) => {
    counts[row.classification] = (counts[row.classification] || 0) + 1;
  });

  return {
    available,
    targetSource,
    rows,
    counts,
    totalOccurrences,
    requiresReview: available && (counts.unresolved > 0 || counts.review > 0),
    newEngineers: rows
      .filter((row) => row.classification === "new")
      .map((row) => row.name),
    message: available
      ? `Using ${targetSource} targets.`
      : "Reconciliation targets are unavailable; names have not been verified.",
  };
}
